import { createReducer, createAction } from '@reduxjs/toolkit';
import { backendCall, createApiCall } from '@reducers/utils';
import type { AppDispatch } from '@reducers/utils/types';
import type { RootState } from '@store/types';
import type { DocumentSearchResponse } from '@api/domain/types';
import type { ImagesState, ImagesData, SearchDocumentsParams } from './types';

// Re-export types for consumers
export type { ImagesState, ImagesData, ImagesDataItem, SearchDocumentsParams } from './types';

const initState: ImagesState = {
  loaded: false,
  canUpload: true,
  data: {
    images: [],
    blobAccessInfo: {},
  },
  page: 0,
  processing: false,
  showLoading: true,
  isError: false,
  pageSize: 30,
  pagination: {
    HasNext: true,
    TotalRecords: 0,
  },
};

export const set = createAction<Partial<ImagesState>>('images/set');
export const updatePage = createAction('images/updatePage');
export const resetPage = createAction('images/resetPage');
export const setError = createAction('images/setError');
export const clearImages = createAction('images/clearImages');
export const clear = createAction('clear');

// selectors
export const selectImages = (state: RootState) => state.images;

export const searchDocuments =
  (callback: (() => void) | undefined, { orderBy, orderByDirection, resetPages, searchValue, resetRecords }: SearchDocumentsParams) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (resetPages) {
      await dispatch(resetPage());
    } else {
      await dispatch(updatePage());
    }

    const {
      images: { page, pageSize, data },
    } = getState();

    const params = {
      search: searchValue,
      top: pageSize,
      skip: page * pageSize,
      orderBy: [`${orderBy} ${orderByDirection}`],
    };

    return await dispatch(
      backendCall(
        createApiCall((api) => api.imageHandling.searchDocuments, {
          params,
          onSuccess: (d: DocumentSearchResponse) => {
            let images: ImagesData['images'];
            if (resetRecords) {
              images = d.searchResult ?? [];
            } else {
              images = [...data.images, ...(d.searchResult ?? [])];
            }

            dispatch(set({ data: { ...data, images, totalResultCount: d.totalResultCount } }));
            if (callback) {
              callback();
            }
          },
          onError: (error: unknown) => {
            console.log(error, 'error');
            dispatch(setError());
          },
        })
      )
    );
  };

export default createReducer<ImagesState>(JSON.parse(JSON.stringify(initState)), {
  [set.type]: (state, action: ReturnType<typeof set>) => ({
    ...state,
    loaded: true,
    processing: false,
    ...action.payload,
  }),
  [updatePage.type]: (state) => ({
    ...state,
    page: state.page + 1,
    processing: true,
  }),
  [resetPage.type]: (state) => ({
    ...state,
    page: 0,
    processing: true,
  }),
  [setError.type]: (state) => ({
    ...state,
    isError: true,
  }),
  [clearImages.type]: () => {
    return initState;
  },
  [clear.type]: () => {
    return initState;
  },
});
